import React, { memo } from 'react';
import { useReactiveVar } from '@apollo/client';

import styled from 'styled-components';
import { CLIENT_WIDTH, desktop, tablet } from 'services/ui';
import { useWindowSize } from 'services/hooks';
import { Colors, UserRole } from 'context';
import { authState } from 'core';

import { getGroupService, isHiddenScheduleGroup } from '../../../config/groupOverrides';
import { CourseInfo, ICourseInfo } from './CourseInfo';
import { Lessons } from './Lessons';
import { MobileLessons } from './MobileLessons';

export interface ICourseProgram extends ICourseInfo {
  activeLessonID?: string;
  children?: React.ReactElement;
}

export const CourseProgram = memo(
  ({
    courseName,
    groupName,
    studentsCount,
    passedLessonsCount,
    courseImageURL,
    lessons,
    lessonsCount,
    groupId,
  }: ICourseProgram) => {
    const { width } = useWindowSize();
    const { active_role: activeRole } = useReactiveVar(authState);
    // Единственная точка вычисления для страницы курса: имя группы есть только
    // здесь. Расписание прячем ТОЛЬКО студенту — методисту и преподавателю
    // даты, номера и счётчики нужны для работы.
    const hideSchedule = activeRole === UserRole.STUDENT && isHiddenScheduleGroup(groupName);
    const service = getGroupService(groupName);
    return (
      <CourseProgramContainer>
        <CourseInfo
          courseImageURL={courseImageURL}
          courseName={courseName}
          groupName={groupName}
          studentsCount={studentsCount}
          passedLessonsCount={passedLessonsCount}
          lessons={lessons}
          lessonsCount={lessonsCount}
          // ТЗ 0026: без этого проброса весь срез «моя посещаемость» был
          // мёртвым кодом. `Course.tsx` передавал groupId сюда, а этот
          // компонент деструктурировал пропсы поимённо и молча его съедал,
          // из-за чего useMyAttendance('') всегда уходил в skip.
          groupId={groupId}
          hideSchedule={hideSchedule}
          service={service}
        />
        {width && width >= CLIENT_WIDTH.DESKTOP ? (
          <Lessons lessons={lessons} hideSchedule={hideSchedule} />
        ) : (
          <MobileLessons lessons={lessons} hideSchedule={hideSchedule} />
        )}
      </CourseProgramContainer>
    );
  },
);

const CourseProgramContainer = styled.div`
  background-color: ${Colors.bg_gray};
  border-radius: 16px;
  padding: 24px 20px;
  margin-bottom: 20px;

  ${tablet} {
    border-radius: 24px;
    padding: 32px;
  }

  ${desktop} {
    background-color: transparent;
    border-radius: 0;
    padding: 0;
    margin-bottom: 0;
    width: 301px;
  }
`;
